import axios from 'axios'
import { jwtDecode } from 'jwt-decode'
import { getTokenExpirationDate } from '@/utils/auth.js'
import { clearSentryUserContext } from '@/utils/log.js'
import { AUTH_PAGES_SWITCH, goToAuthPage } from '@/utils/authPages.js'
import { AUTH_OBTAIN_TOKEN, AUTH_REFRESH_TOKEN, PASSWORD_RESET_REQUEST, PASSWORD_RESET_VALIDATE, PASSWORD_RESET_CONFIRM } from '@/api/resources.js'

const localStorageAccessKey = 'accessToken'
const localStorageRefreshKey = 'refreshToken'

// mutation types
const SET_ACCESS_TOKEN = 'SET_ACCESS_TOKEN'
const SET_REFRESH_TOKEN = 'SET_REFRESH_TOKEN'
const CLEAR_ACCESS_TOKEN = 'CLEAR_ACCESS_TOKEN'
const CLEAR_REFRESH_TOKEN = 'CLEAR_REFRESH_TOKEN'
const SET_REFRESH_TOKEN_TIMEOUT = 'SET_REFRESH_TOKEN_TIMEOUT'
const CLEAR_REFRESH_TOKEN_TIMEOUT = 'CLEAR_REFRESH_TOKEN_TIMEOUT'
const SET_SHOW_LOGIN_MODAL = 'SET_SHOW_LOGIN_MODAL'
const SET_SHOW_PASSWORD_RESET_MODAL = 'SET_SHOW_PASSWORD_RESET_MODAL'
const SET_REDIRECT_TO = 'SET_REDIRECT_TO'

// initial state
const state = {
  accessToken: localStorage.getItem(localStorageAccessKey),
  refreshToken: localStorage.getItem(localStorageRefreshKey),
  refreshTokenTimeout: null,
  showLoginModal: false,
  showPasswordResetModal: false,
  redirectTo: ''
}

// getters
const getters = {
  accessToken: (state) => {
    return state.accessToken
  },

  refreshToken: (state) => {
    return state.refreshToken
  },

  isLoggedIn: (state) => {
    if (state.accessToken === null) {
      return false
    }
    const expirationDate = getTokenExpirationDate(state.accessToken)
    const expired = expirationDate < new Date()
    return !!state.accessToken && !expired
  },

  showLoginModal: (state) => {
    return state.showLoginModal
  },

  showPasswordResetModal: (state) => {
    return state.showPasswordResetModal
  },

  redirectTo: (state) => {
    return state.redirectTo
  }
}

// actions
const actions = {
  login: (context, payload) => {
    return axios.post(`${AUTH_OBTAIN_TOKEN}`, payload)
      .then((response) => {
        context.commit(SET_ACCESS_TOKEN, response.data.access)
        context.commit(SET_REFRESH_TOKEN, response.data.refresh)
        context.dispatch('player/clearUnauthenticatedPlayCount', null, { root: true })
        context.dispatch('startRefreshTokenTimer')
      })
  },

  logout: (context) => {
    context.dispatch('stopRefreshTokenTimer')
    context.commit(CLEAR_ACCESS_TOKEN)
    context.commit(CLEAR_REFRESH_TOKEN)
    context.dispatch('favouriteTracks/clearFavouriteTracks', null, { root: true })
    context.dispatch('licenses/clearLicenses', null, { root: true })
    context.dispatch('sfxDownloadLog/clearLogEntries', null, { root: true })
    context.dispatch('collections/clearCollections', null, { root: true })
    context.dispatch('user/clearCurrentObjects', null, { root: true })
    context.dispatch('subscriptions/clearCurrentObjects', null, { root: true })
    clearSentryUserContext()
  },

  startRefreshTokenTimer: (context) => {
    const token = jwtDecode(context.getters.accessToken)
    const expires = new Date(token.exp * 1000)
    // Delay is expiry minus a minute.
    const delay = expires.getTime() - Date.now() - (60 * 1000)
    context.commit(SET_REFRESH_TOKEN_TIMEOUT, setTimeout(() => {
      context.dispatch('refreshToken')
    }, delay))
  },

  refreshToken: (context) => {
    return axios.post(`${AUTH_REFRESH_TOKEN}`, { refresh: context.getters.refreshToken })
      .then((response) => {
        context.commit(SET_ACCESS_TOKEN, response.data.access)
        context.dispatch('startRefreshTokenTimer')
      })
  },

  stopRefreshTokenTimer: (context) => {
    clearTimeout(context.state.refreshTokenTimeout)
    context.commit(CLEAR_REFRESH_TOKEN_TIMEOUT)
  },

  passwordResetRequest: (context, email) => {
    return axios.post(`${PASSWORD_RESET_REQUEST}`, { email })
  },

  passwordResetValidate: (context, token) => {
    return axios.post(`${PASSWORD_RESET_VALIDATE}`, { token })
  },

  passwordResetConfirm: (context, payload) => {
    // payload: { token, password }
    return axios.post(`${PASSWORD_RESET_CONFIRM}`, payload)
  },

  showLoginModal: (context) => {
    // Wait for the switch so an early click doesn't open the modal it replaces.
    // If waffle can't load, fall back to the modal.
    return context.dispatch('waffle/ensureLoaded', null, { root: true })
      .catch(() => {})
      .then(() => {
        if (context.rootGetters['waffle/isFeatureEnabled'](AUTH_PAGES_SWITCH)) {
          // The page takes the destination as ?next=, so don't leave it in the store too.
          const redirectTo = context.getters.redirectTo
          context.dispatch('clearRedirectTo')
          // Imported here because the router imports the store.
          return import('@/router.js').then(({ default: router }) => goToAuthPage(router, 'login', redirectTo))
        }
        context.commit(SET_SHOW_LOGIN_MODAL, true)
      })
  },

  hideLoginModal: (context) => {
    context.commit(SET_SHOW_LOGIN_MODAL, false)
  },

  showPasswordResetModal: (context) => {
    context.commit(SET_SHOW_PASSWORD_RESET_MODAL, true)
  },

  hidePasswordResetModal: (context) => {
    context.commit(SET_SHOW_PASSWORD_RESET_MODAL, false)
  },

  setRedirectTo: (context, payload) => {
    context.commit(SET_REDIRECT_TO, payload)
  },

  clearRedirectTo: (context) => {
    context.commit(SET_REDIRECT_TO, '')
  }
}

// mutations
const mutations = {
  [SET_ACCESS_TOKEN]: (state, token) => {
    localStorage.setItem(localStorageAccessKey, token)
    state.accessToken = token
  },

  [SET_REFRESH_TOKEN]: (state, token) => {
    localStorage.setItem(localStorageRefreshKey, token)
    state.refreshToken = token
  },

  [CLEAR_ACCESS_TOKEN]: (state) => {
    localStorage.removeItem(localStorageAccessKey)
    state.accessToken = null
  },

  [CLEAR_REFRESH_TOKEN]: (state) => {
    localStorage.removeItem(localStorageRefreshKey)
    state.refreshToken = null
  },

  [SET_REFRESH_TOKEN_TIMEOUT]: (state, timeout) => {
    state.refreshTokenTimeout = timeout
  },

  [CLEAR_REFRESH_TOKEN_TIMEOUT]: (state) => {
    state.refreshTokenTimeout = null
  },

  [SET_SHOW_LOGIN_MODAL]: (state, payload) => {
    state.showLoginModal = payload
  },

  [SET_SHOW_PASSWORD_RESET_MODAL]: (state, payload) => {
    state.showPasswordResetModal = payload
  },

  [SET_REDIRECT_TO]: (state, payload) => {
    state.redirectTo = payload
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
